import type { BottomTabNavigationOptions } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { createModalScreenOptions } from 'app/lib/navigation';
import { usePopularItemsByTag } from 'app/network/category';
import { useCreateManyInterests } from 'app/network/interests';
import { useAllTags, useClosestOffersByTag, type Tag } from 'app/network/listing';
import type { PopularItem } from 'app/types/category';
import type { SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { SharerType } from 'app/types/preferences';
import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { useRef, useState } from 'react';
import type { FlatList, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { Platform } from 'react-native';
import { InterestsStepName, type InterestsComponentProps } from './interests-component';

const navigateToMain = (nav: SpecificNavigationProp<'interests'>) => {
  nav.reset({ routes: [{ name: 'main' }] });
};

export function useInterestsScreen({
  navigation,
  route,
  itemWidth,
  webAggregateListingSteps = false,
  webListingLayout,
}: {
  navigation: SpecificNavigationProp<'interests'>;
  route: SpecificRouteProp<'interests'>;
  itemWidth: number;
  webAggregateListingSteps?: boolean;
  webListingLayout?: 'small' | 'large';
}) {
  const { tagsEnabled } = useListingCatalog();
  const indexShift = route.params.skipExplain ? 1 : 2;
  // Deep link /intro/quick-publish has no params; default to lender (publish flow).
  const sharerType = route.params.sharerType ?? SharerType.lender;
  const isComingFromBabillard = route.params.fromBabillard ?? false;
  const { t } = useScopedTranslation('interests');

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedTags, setSelectedTags] = useState<Tag['id'][]>([]);
  const [selectedPopularItems, setSelectedPopularItems] = useState<PopularItem[]>([]);
  const [selectedListings, setSelectedListings] = useState<string[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const { data: allTagsData, isPending: isAllTagsPending } = useAllTags();
  const { mutate: createManyInterests } = useCreateManyInterests();

  const {
    data: popularItemsByTag,
    isFetching: isPopularItemsFetching,
    isPending: isPopularItemsPending,
  } = usePopularItemsByTag({
    enabled: sharerType === SharerType.lender,
  });
  const {
    data: closestOffersByTag,
    isFetching: isClosestOffersByTagFetching,
    isPending: isClosestOffersByTagPending,
  } = useClosestOffersByTag(undefined, {
    enabled: sharerType === SharerType.borrower,
  });

  useRegisterLoading(isPopularItemsPending && sharerType === SharerType.lender, 'InterestsScreen');
  useRegisterLoading(isClosestOffersByTagPending && sharerType === SharerType.borrower, 'InterestsScreen');

  const isTagsPending =
    isAllTagsPending || (sharerType === SharerType.lender ? isPopularItemsPending : isClosestOffersByTagPending);

  const tagsById = new Map(allTagsData?.map((tag) => [tag.id, tag]));
  const allTags = (() => {
    const rankedTags = sharerType === SharerType.lender ? popularItemsByTag : closestOffersByTag;
    return rankedTags?.flatMap(({ tagId }) => {
      const tag = tagsById.get(tagId);
      return tag ? [tag] : [];
    });
  })();

  const carouselItems = (() => {
    const items: Omit<
      InterestsComponentProps,
      | 'allTags'
      | 'setSelectedTags'
      | 'currentIndex'
      | 'sharerType'
      | 'selectedPopularItems'
      | 'setSelectedPopularItems'
      | 'scrollToIndex'
      | 'selectedListings'
      | 'setSelectedListings'
      | 'onWebAggregateClose'
    >[] = [];

    if (tagsEnabled) {
      if (!route.params.skipExplain) {
        items.push({
          stepName: InterestsStepName.Explain,
          index: 0,
          selectedTags,
        });
      }
      items.push({
        stepName: InterestsStepName.ChooseTag,
        index: indexShift - 1,
        selectedTags,
      });
      if (webAggregateListingSteps && selectedTags.length > 0) {
        items.push({
          stepName: InterestsStepName.ChooseListing,
          index: indexShift,
          selectedTags,
          webAggregateListings: true,
          webListingLayout,
        });
      } else {
        selectedTags.forEach((_, idx) => {
          items.push({
            stepName: InterestsStepName.ChooseListing,
            index: indexShift + idx,
            selectedTags,
          });
        });
      }
    }

    return items;
  })();

  const flatListRef = useRef<FlatList>(null);

  const scrollToIndex = (index: number) => {
    flatListRef.current?.scrollToIndex({ index, animated: true });
    setCurrentIndex(index);
  };

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const newIndex = Math.round(e.nativeEvent.contentOffset.x / itemWidth);
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < carouselItems.length) {
      setCurrentIndex(newIndex);
    }
  };

  const handleContinue = () => {
    if (currentIndex < carouselItems.length - 1) {
      if (carouselItems[currentIndex]?.stepName === InterestsStepName.ChooseTag) {
        const event = sharerType === SharerType.borrower ? Event.ChooseTagsAsBorrower : Event.ChooseTagsAsLender;
        const selectedTagsNames = selectedTags
          .map((tagId) => allTags?.find((at) => at.id === tagId)?.tagName)
          .filter((item): item is string => item !== undefined);
        track(event, {
          num_selected: selectedTags.length,
          tags_selected: selectedTagsNames,
          source: isComingFromBabillard ? 'new_listing' : 'onboarding',
        });
        createManyInterests(
          {
            type: 'interest',
            action: sharerType === SharerType.borrower ? 'borrow' : 'lend',
            tagIds: selectedTags,
          },
          {
            onSuccess: () => scrollToIndex(currentIndex + 1),
            onError: () => setShowErrorModal(true),
          },
        );
      } else {
        scrollToIndex(currentIndex + 1);
      }
    } else if (sharerType === SharerType.lender) {
      if (selectedPopularItems.length > 0) {
        if (Platform.OS === 'web') {
          try {
            sessionStorage.setItem('quick-new-selected-items', JSON.stringify(selectedPopularItems));
          } catch {}
        }
        navigation.navigate(
          'quickNew',
          {
            screen: 'quick-new-confirm',
            params: { selectedItems: selectedPopularItems },
          },
          { pop: true },
        );
      } else {
        setShowErrorModal(true);
      }
    } else {
      navigateToMain(navigation);
    }
  };

  const handleSkip = () => {
    const stepName = carouselItems[currentIndex]?.stepName;
    if (stepName) {
      const where =
        stepName === InterestsStepName.Explain
          ? 'onboarding_flow_intro'
          : stepName === InterestsStepName.ChooseTag
            ? 'onboarding_flow_tags'
            : 'onboarding_flow_listings';
      track(Event.GiveUpOnboarding, {
        where,
        page_number: stepName === InterestsStepName.ChooseListing ? currentIndex - 1 : undefined,
      });
    }

    if (!route.params.skipExplain || !isComingFromBabillard) {
      navigateToMain(navigation);
    } else {
      navigation.goBack();
    }
  };

  const currentStep = carouselItems[currentIndex];

  const headerTitle =
    route.params.skipExplain && sharerType === SharerType.borrower
      ? t('headerTitle', { ns: 'borrowerNext' })
      : route.params.skipExplain && sharerType === SharerType.lender
        ? t('headerTitle', { ns: 'lenderNext' })
        : t('headerTitle');

  const navigationOptions = ((): NativeStackNavigationOptions => {
    const modalOptions = createModalScreenOptions(navigation, route);
    return {
      ...modalOptions,
      headerShown: false,
    };
  })();

  useNavigationOptions(navigation, navigationOptions as BottomTabNavigationOptions & NativeStackNavigationOptions);

  return {
    allTags,
    t,
    carouselItems,
    closestOffersByTag,
    currentIndex,
    currentStep,
    flatListRef,
    handleContinue,
    handleScroll,
    handleSkip,
    headerTitle,
    indexShift,
    isClosestOffersByTagFetching,
    isComingFromBabillard,
    isPopularItemsFetching,
    isTagsPending,
    itemWidth,
    popularItemsByTag,
    selectedListings,
    selectedPopularItems,
    selectedTags,
    setSelectedListings,
    setSelectedPopularItems,
    setSelectedTags,
    setShowErrorModal,
    sharerType,
    showErrorModal,
    scrollToIndex,
  };
}

export type InterestsScreenState = ReturnType<typeof useInterestsScreen>;
